import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
  Chip,
  Alert,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  QrCode as QrCodeIcon,
  Download as DownloadIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import axiosInstance from '../../utils/axios';
import { RootState } from '../../store';
import { QRCode, UserRole, Company, Individual, Category } from '../../types';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import Autocomplete from '@mui/material/Autocomplete';
import { QRCodeSVG } from 'qrcode.react';

interface QRCodeFormValues {
  serviceName: string;
  companyId?: string;
  individualId?: string;
  categoryId: string;
  isActive: boolean;
}

const validationSchema = Yup.object({
  serviceName: Yup.string().required('El nombre del servicio es requerido'),
  categoryId: Yup.string().required('La categoría es requerida'),
  companyId: Yup.string().optional(),
  individualId: Yup.string().optional(),
  isActive: Yup.boolean()
}).test('company-or-individual', 'Debe seleccionar una empresa o un particular', function(value) {
  const { companyId, individualId } = value;
  return Boolean((companyId && !individualId) || (!companyId && individualId));
});

const QRCodeManagement: React.FC = () => {
  const { user } = useSelector((state: RootState) => state.auth);
  const isDirector = user?.role === UserRole.DIRECTOR;
  const [qrCodes, setQrCodes] = useState<QRCode[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [individuals, setIndividuals] = useState<Individual[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedQRCode, setSelectedQRCode] = useState<QRCode | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewQRCode, setPreviewQRCode] = useState<QRCode | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchQRCodes();
    fetchCompanies();
    fetchIndividuals();
    fetchCategories();
  }, []);

  const fetchQRCodes = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/qr-codes');
      if (response.data && Array.isArray(response.data)) {
        setQrCodes(response.data);
        setError(null);
      } else {
        console.error('Invalid response format:', response.data);
        setError('Formato de respuesta inválido');
        setQrCodes([]);
      }
    } catch (error: any) {
      console.error('Error fetching QR codes:', error);
      setError(error.response?.data?.message || 'Error al cargar los códigos QR');
      setQrCodes([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axiosInstance.get('/companies');
      if (response.data && Array.isArray(response.data)) {
        setCompanies(response.data);
      } else {
        setCompanies([]);
      }
    } catch (error: any) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    }
  };

  const fetchIndividuals = async () => {
    try {
      const response = await axiosInstance.get('/individuals');
      if (response.data && Array.isArray(response.data)) {
        setIndividuals(response.data);
      } else {
        setIndividuals([]);
      }
    } catch (error: any) {
      console.error('Error fetching individuals:', error);
      setIndividuals([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axiosInstance.get('/categories');
      if (response.data && Array.isArray(response.data)) {
        setCategories(response.data);
      } else {
        setCategories([]);
      }
    } catch (error) {
      setCategories([]);
    }
  };

  const handleOpenDialog = (qrCode?: QRCode) => {
    setSelectedQRCode(qrCode || null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedQRCode(null);
  };

  const handleSubmit = async (values: QRCodeFormValues) => {
    try {
      if (selectedQRCode) {
        await axiosInstance.put(`/qr-codes/${selectedQRCode.id}`, values);
      } else {
        await axiosInstance.post('/qr-codes', values);
      }
      fetchQRCodes();
      handleCloseDialog();
      setError(null);
    } catch (error: any) {
      console.error('Error saving QR code:', error);
      setError(error.response?.data?.message || 'Error al guardar el código QR');
    }
  };

  const handleDelete = async (qrCodeId: string) => {
    if (window.confirm('¿Estás seguro de que deseas eliminar este código QR?')) {
      try {
        await axiosInstance.delete(`/qr-codes/${qrCodeId}`);
        fetchQRCodes();
        setError(null);
      } catch (error: any) {
        console.error('Error deleting QR code:', error);
        setError(error.response?.data?.message || 'Error al eliminar el código QR');
      }
    }
  };

  const handleDownloadPDF = async (qrCodeId: string, serviceName: string) => {
    try {
      const response = await axiosInstance.get(`/qr-codes/${qrCodeId}/pdf`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `QR-${serviceName.replace(/\s+/g, '-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading PDF:', error);
      setError(error.response?.data?.message || 'Error al descargar el PDF');
    }
  };

  const handlePreviewQRCode = (qrCode: QRCode) => {
    setPreviewQRCode(qrCode);
    setOpenPreviewDialog(true);
  };

  const filteredQRCodes = qrCodes.filter(qr => {
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        qr.serviceName.toLowerCase().includes(searchLower) ||
        qr.company?.name.toLowerCase().includes(searchLower) ||
        qr.individual?.name.toLowerCase().includes(searchLower) ||
        qr.category?.name.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const columns: GridColDef[] = [
    { 
      field: 'serviceName', 
      headerName: 'Servicio', 
      flex: 1,
      minWidth: isMobile ? 120 : 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" noWrap>
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'client', 
      headerName: 'Cliente', 
      flex: 1,
      minWidth: isMobile ? 100 : 120,
      valueGetter: (params) => {
        if (params.row.company?.name) {
          return `🏢 ${params.row.company.name}`;
        } else if (params.row.individual?.name) {
          return `👤 ${params.row.individual.name}`;
        }
        return '';
      },
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" noWrap>
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'category', 
      headerName: 'Categoría', 
      flex: 1,
      minWidth: isMobile ? 100 : 120,
      valueGetter: (params) => params.row.category?.name || '',
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" noWrap>
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'isActive', 
      headerName: 'Estado', 
      flex: 1,
      minWidth: isMobile ? 80 : 100,
      renderCell: (params: GridRenderCellParams) => (
        <Chip
          label={params.value ? 'Activo' : 'Inactivo'}
          color={params.value ? 'success' : 'default'}
          size="small"
          sx={{ fontSize: isMobile ? '0.7rem' : '0.75rem' }}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      flex: 1,
      minWidth: isMobile ? 120 : 200,
      renderCell: (params: GridRenderCellParams) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton 
            size="small" 
            color="primary"
            onClick={() => handlePreviewQRCode(params.row)}
            title="Ver QR"
          >
            <QrCodeIcon />
          </IconButton>
          <IconButton 
            size="small" 
            color="success"
            onClick={() => handleDownloadPDF(params.row.id, params.row.serviceName)}
            title="Descargar PDF"
          >
            <DownloadIcon />
          </IconButton>
          {isDirector && (
            <>
              <IconButton 
                size="small" 
                color="primary"
                onClick={() => handleOpenDialog(params.row)}
                title="Editar"
              >
                <EditIcon />
              </IconButton>
              <IconButton 
                size="small" 
                color="error"
                onClick={() => handleDelete(params.row.id)}
                title="Eliminar"
              >
                <DeleteIcon />
              </IconButton>
            </>
          )}
        </Stack>
      )
    }
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', boxSizing: 'border-box', p: { xs: 2, sm: 3 } }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexDirection={isMobile ? 'column' : 'row'}
        gap={isMobile ? 2 : 0}
      >
        <Typography
          variant="h4"
          sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' }, textAlign: { xs: 'center', sm: 'left' }, width: { xs: '100%', sm: 'auto' } }}
        >
          Gestión de Códigos QR
        </Typography>
        {isDirector && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            fullWidth={isMobile}
            sx={{ borderRadius: 2 }}
          >
            Nuevo QR
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box mb={3}>
        <TextField
          label="Buscar"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          fullWidth
          size={isMobile ? 'small' : 'medium'}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
          }}
        />
      </Box>

      <div style={{ width: '100%', overflowX: 'auto' }}>
        <Box sx={{ minWidth: { xs: 350, sm: 700 }, width: '100%' }}>
          <DataGrid
            rows={filteredQRCodes}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50]}
            getRowId={row => row.id}
            sx={{
              minWidth: { xs: 350, sm: 700 },
              width: '100%',
              '& .MuiDataGrid-cell': {
                fontSize: isMobile ? '0.75rem' : '0.875rem',
              },
              '& .MuiDataGrid-columnHeader': {
                fontSize: isMobile ? '0.75rem' : '0.875rem',
              }
            }}
          />
        </Box>
      </div>

      {/* Diálogo para crear/editar QR */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: { xs: '95vw', sm: 600 },
            m: { xs: 1, sm: 3 },
          }
        }}
      >
        <DialogTitle>
          {selectedQRCode ? 'Editar Código QR' : 'Nuevo Código QR'}
        </DialogTitle>
        <Formik
          initialValues={{
            serviceName: selectedQRCode?.serviceName || '',
            companyId: selectedQRCode?.companyId || '',
            individualId: selectedQRCode?.individualId || '',
            categoryId: selectedQRCode?.categoryId || '',
            isActive: selectedQRCode?.isActive ?? true
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ isSubmitting, setFieldValue, values, errors, touched }) => (
            <Form>
              <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      fullWidth
                      margin="normal"
                      name="serviceName"
                      label="Nombre del Servicio"
                      autoComplete="off"
                      error={touched.serviceName && Boolean(errors.serviceName)}
                      helperText={touched.serviceName && errors.serviceName}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>
                      Cliente
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          options={companies}
                          getOptionLabel={(option) => option.name}
                          value={companies.find(c => c.id === values.companyId) || null}
                          onChange={(_, newValue) => {
                            setFieldValue('companyId', newValue ? newValue.id : '');
                            setFieldValue('individualId', '');
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Empresa"
                              margin="normal"
                              fullWidth
                              error={touched.companyId && Boolean(errors.companyId)}
                              helperText={touched.companyId && errors.companyId}
                            />
                          )}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Autocomplete
                          options={individuals}
                          getOptionLabel={(option) => option.name}
                          value={individuals.find(i => i.id === values.individualId) || null}
                          onChange={(_, newValue) => {
                            setFieldValue('individualId', newValue ? newValue.id : '');
                            setFieldValue('companyId', '');
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Particular"
                              margin="normal"
                              fullWidth
                              error={touched.individualId && Boolean(errors.individualId)}
                              helperText={touched.individualId && errors.individualId}
                            />
                          )}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item xs={12}>
                    <Autocomplete
                      options={categories}
                      getOptionLabel={(option) => option.name}
                      value={categories.find(c => c.id === values.categoryId) || null}
                      onChange={(_, newValue) => setFieldValue('categoryId', newValue ? newValue.id : '')}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Categoría"
                          margin="normal"
                          fullWidth
                          error={touched.categoryId && Boolean(errors.categoryId)}
                          helperText={touched.categoryId && typeof errors.categoryId === 'string' ? errors.categoryId : ''}
                        />
                      )}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={values.isActive}
                          onChange={(e) => setFieldValue('isActive', e.target.checked)}
                        />
                      }
                      label="Activo"
                    />
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog}>Cancelar</Button>
                <Button type="submit" variant="contained" disabled={isSubmitting}>
                  {isSubmitting ? <CircularProgress size={24} /> : (selectedQRCode ? 'Guardar Cambios' : 'Crear QR')}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>

      {/* Diálogo para previsualizar QR */}
      <Dialog
        open={openPreviewDialog}
        onClose={() => setOpenPreviewDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Código QR - {previewQRCode?.serviceName}</DialogTitle>
        <DialogContent>
          {previewQRCode && (
            <Box display="flex" flexDirection="column" alignItems="center" p={2}>
              <QRCodeSVG
                value={`${window.location.origin}/scan-qr/${previewQRCode.id}`}
                size={300}
                level="H"
                includeMargin
              />
              <Typography variant="body2" color="text.secondary" mt={2} textAlign="center">
                Cliente: {previewQRCode.company?.name || previewQRCode.individual?.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Categoría: {previewQRCode.category?.name}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPreviewDialog(false)}>Cerrar</Button>
          {previewQRCode && (
            <Button 
              variant="contained" 
              startIcon={<DownloadIcon />}
              onClick={() => {
                handleDownloadPDF(previewQRCode.id, previewQRCode.serviceName);
                setOpenPreviewDialog(false);
              }}
            >
              Descargar PDF
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default QRCodeManagement;

